import { InjectionToken } from '@angular/core';
import {
  confirmResetPassword as amplifyConfirmResetPassword,
  confirmSignIn as amplifyConfirmSignIn,
  fetchAuthSession,
  resetPassword as amplifyResetPassword,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  updatePassword as amplifyUpdatePassword,
} from 'aws-amplify/auth';

export interface SignInOutcome {
  readonly isSignedIn: boolean;
  readonly nextStep: string;
}

export interface ResetPasswordOutcome {
  readonly isPasswordReset: boolean;
  readonly nextStep: string;
}

export interface CognitoAuthClient {
  signIn(username: string, password: string): Promise<SignInOutcome>;
  confirmSignIn(challengeResponse: string): Promise<SignInOutcome>;
  startPasswordReset(username: string): Promise<ResetPasswordOutcome>;
  confirmPasswordReset(
    username: string,
    confirmationCode: string,
    newPassword: string,
  ): Promise<void>;
  updatePassword(oldPassword: string, newPassword: string): Promise<void>;
  signOut(): Promise<void>;
  getAccessToken(): Promise<string | null>;
}

export const COGNITO_AUTH_CLIENT = new InjectionToken<CognitoAuthClient>(
  'COGNITO_AUTH_CLIENT',
  {
    providedIn: 'root',
    factory: () => ({
      async signIn(username, password) {
        const result = await amplifySignIn({
          username,
          password,
          options: { authFlowType: 'USER_SRP_AUTH' },
        });
        return {
          isSignedIn: result.isSignedIn,
          nextStep: result.nextStep.signInStep,
        };
      },
      async confirmSignIn(challengeResponse) {
        const result = await amplifyConfirmSignIn({ challengeResponse });
        return {
          isSignedIn: result.isSignedIn,
          nextStep: result.nextStep.signInStep,
        };
      },
      async startPasswordReset(username) {
        const result = await amplifyResetPassword({ username });
        return {
          isPasswordReset: result.isPasswordReset,
          nextStep: result.nextStep.resetPasswordStep,
        };
      },
      async confirmPasswordReset(username, confirmationCode, newPassword) {
        await amplifyConfirmResetPassword({
          username,
          confirmationCode,
          newPassword,
        });
      },
      async updatePassword(oldPassword, newPassword) {
        await amplifyUpdatePassword({ oldPassword, newPassword });
      },
      async signOut() {
        await amplifySignOut();
      },
      async getAccessToken() {
        const session = await fetchAuthSession();
        return session.tokens?.accessToken?.toString() ?? null;
      },
    }),
  },
);
